import { create } from 'zustand'
import type { SelectedTankArray, TankStockValuesArray } from '@/types/view'
import type { ValidationError } from '@/utils/assignGroupValidation'

export type GroupSelection = { groupId: string | null; groupName: string | null }

type HomeStockState = {
  selectedTanks: SelectedTankArray
  tanksValues: TankStockValuesArray
  selectTanksBorderColor: string
  tanksInfoBorderColor: string
  stockData: TankStockValuesArray | null
  typeError: boolean
  requiredError: boolean
  groupSelections: Record<number, GroupSelection>
  validationErrors: ValidationError[]
  pendingCreateGroupRowIndex: number | null
}

type HomeStockAction = {
  setSelectedTanks: (selectedTanks: SelectedTankArray) => void
  setBorderColor: () => void
  setTanksValues: (tanksValues: TankStockValuesArray) => void
  setTanksInfoBorderColor: () => void
  setGroupForRow: (rowIndex: number, groupId: string | null, groupName: string | null) => void
  setBatchInputForRow: (rowIndex: number, value: string) => void
  setPendingCreateGroupRowIndex: (rowIndex: number | null) => void
  applyNewGroupToRow: (groupId: string, groupName: string) => void
  setValidationErrors: (errors: ValidationError[]) => void
  clearValidationErrors: () => void
  reset: () => void
}

const toStockData = (values: TankStockValuesArray): TankStockValuesArray =>
  values.map((item) => ({
    tankId: item.tankId,
    tankName: item.tankName,
    batchId: item.batchId,
    batchName: item.batchName,
    batchInput: item.batchInput,
    count: item.count,
    moveDate: item.moveDate,
    sampleWeight: item.sampleWeight,
  }))

const initialState: HomeStockState = {
  selectedTanks: [],
  tanksValues: [],
  selectTanksBorderColor: 'border border-tdpldarkgrey',
  tanksInfoBorderColor: 'border border-tdpldarkgrey',
  stockData: null,
  typeError: false,
  requiredError: false,
  groupSelections: {},
  validationErrors: [],
  pendingCreateGroupRowIndex: null,
}

const useHomeStockStore = create<HomeStockState & HomeStockAction>((set, get) => ({
  ...initialState,
  setSelectedTanks: (selectedTanks) => {
    const groupSelections: Record<number, GroupSelection> = {}
    selectedTanks.forEach((_, index) => {
      groupSelections[index] = { groupId: null, groupName: null }
    })
    set(() => ({
      selectedTanks: selectedTanks,
      selectTanksBorderColor: 'border border-tdpldarkgrey',
      groupSelections,
      validationErrors: [],
      pendingCreateGroupRowIndex: null,
      tanksValues: selectedTanks.map((item) => {
        return {
          tankId: item.tankId,
          tankName: item.tankName,
          batchId: item.batchId,
          batchName: item.batchName,
          batchInput: null,
          count: null,
          moveDate: null,
          sampleWeight: null,
        }
      }),
    }))
  },
  setTanksValues: (tanksValues) => {
    set(() => {
      const typeError = !tanksValues.every(
        (item) => item.count === null || Number.isInteger(item.count),
      )
      return {
        tanksValues: tanksValues,
        tanksInfoBorderColor: typeError
          ? 'border-2 border-tdplmaroon'
          : 'border border-tdpldarkgrey',
        requiredError: false,
        stockData: toStockData(tanksValues),
        typeError: typeError,
      }
    })
  },
  setBorderColor: () => {
    set((state) => ({
      selectTanksBorderColor:
        state.selectedTanks.length !== 0
          ? 'border border-tdpldarkgrey'
          : 'border-2 border-tdplmaroon',
    }))
  },
  setTanksInfoBorderColor: () => {
    set((state) => {
      const requiredError = !state.tanksValues.every(
        (item) => item.count !== null && item.moveDate !== null && !!item.batchInput,
      )
      return {
        tanksInfoBorderColor:
          requiredError || state.typeError
            ? 'border-2 border-tdplmaroon'
            : 'border border-tdpldarkgrey',
        requiredError: requiredError,
      }
    })
  },
  setGroupForRow: (rowIndex, groupId, groupName) => {
    const state = get()
    const newGroupSelections = {
      ...state.groupSelections,
      [rowIndex]: { groupId, groupName },
    }
    const newTanksValues = [...state.tanksValues]
    if (newTanksValues[rowIndex]) {
      newTanksValues[rowIndex] = {
        ...newTanksValues[rowIndex],
        batchId: groupId ?? undefined,
        batchName: groupName ?? undefined,
      }
    }
    set({
      groupSelections: newGroupSelections,
      tanksValues: newTanksValues,
      stockData: toStockData(newTanksValues),
    })
  },
  setBatchInputForRow: (rowIndex, value) => {
    const state = get()
    const newTanksValues = [...state.tanksValues]
    if (newTanksValues[rowIndex]) {
      newTanksValues[rowIndex] = {
        ...newTanksValues[rowIndex],
        batchInput: value || null,
      }
    }
    set({
      tanksValues: newTanksValues,
      stockData: toStockData(newTanksValues),
    })
  },
  setPendingCreateGroupRowIndex: (rowIndex) => {
    set({ pendingCreateGroupRowIndex: rowIndex })
  },
  applyNewGroupToRow: (groupId, groupName) => {
    const { pendingCreateGroupRowIndex } = get()
    if (pendingCreateGroupRowIndex !== null) {
      get().setGroupForRow(pendingCreateGroupRowIndex, groupId, groupName)
      set({ pendingCreateGroupRowIndex: null })
    }
  },
  setValidationErrors: (errors) => {
    set({ validationErrors: errors })
  },
  clearValidationErrors: () => {
    set({ validationErrors: [] })
  },
  reset: () => {
    set(initialState)
  },
}))

export default useHomeStockStore
